import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Divider,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import axios from 'axios';
import { url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IAgencyUpdateInfoApplication } from 'src/Types/module/applications/applications';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { LoadingButton } from '@mui/lab';
import { IResponse } from 'src/basic-rest/types';
import { HttpClient } from 'src/Helpers/http/http';

interface IProps {
  setApplication: React.Dispatch<
    React.SetStateAction<IAgencyUpdateInfoApplication>
  >;
  application: IAgencyUpdateInfoApplication;
  handleClose: any;
}

const UpdateAgencyInfoApplication = ({
  setApplication,
  application,
  handleClose,
}: IProps) => {
  const [loading, setLoading] = useState(false);
  const [isChangeEmail, setIsChangeEmail] = useState<boolean>(false);
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    formState: { errors, touchedFields },
    watch,
    getValues,
    reset,
  } = useForm<any>({
    defaultValues: {},
  });

  useEffect(() => {
    reset({
      ...application,
    });
    if (application?.credential_email) {
      setIsChangeEmail(true);
    } else {
      setIsChangeEmail(false);
    }
  }, [application, reset]);

  const watchedAgencyName = watch('agency_name');
  const watchedOwnershipStatus = watch('ownership_status');

  const isAgencyNameChanged = Boolean(
    (watchedAgencyName &&
      watchedAgencyName.trim() !== '' &&
      watchedAgencyName !== application?.user_member_company_name) ||
    (application?.agency_name &&
      application?.agency_name !== application?.user_member_company_name) ||
    watchedOwnershipStatus ||
    application?.ownership_status
  );

  const currentOwnership =
    watchedOwnershipStatus !== undefined
      ? watchedOwnershipStatus
      : application?.ownership_status;

  const onSubmit = async () => {
    const formData = new FormData();
    const allValues = getValues();

    const modifiedKeys = Array.from(
      new Set([
        ...Object.keys(touchedFields || {}),
        ...Object.keys(allValues).filter((key) => {
          const val = allValues[key];
          if (val instanceof FileList || Array.isArray(val)) {
            return val.length > 0 && val[0] instanceof File;
          }
          return (
            val !== undefined &&
            val !== null &&
            val !== (application as any)?.[key]
          );
        }),
      ])
    );

    if (
      isChangeEmail &&
      allValues.credential_email &&
      allValues.credential_email.trim() !== ''
    ) {
      modifiedKeys.push('credential_email');
    }

    const filteredKeys = modifiedKeys.filter((key) => {
      if (!isChangeEmail && key === 'credential_email') {
        return false;
      }
      return true;
    });

    const uniqueKeys = Array.from(new Set(filteredKeys));

    if (uniqueKeys.length === 0) {
      Toaster().fire({
        icon: 'info',
        title: 'No changes detected to update',
      });
      return;
    }

    for (const key of uniqueKeys) {
      const value = allValues[key];

      if (value instanceof FileList || Array.isArray(value)) {
        const file = value[0];
        if (file instanceof File) {
          formData.append(key, file);
        }
      } else if (value !== undefined && value !== null) {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed !== '') {
            formData.append(key, trimmed);
          }
        } else {
          formData.append(key, value);
        }
      }
    }

    setLoading(true);

    try {
      const res: IResponse = await HttpClient.patch(
        `${url}/api/atab/agency-member-info/update/${id}`,
        formData
      );

      if (res.success) {
        Toaster().fire({
          icon: 'success',
          title: res.message || 'Updated successfully',
        });
        handleClose(false);
        const { data } = await axios.get(
          `${url}/api/atab/agency-member-info/${id}`
        );

        if (data.success) {
          setApplication(data.data);
        }
      }
    } catch (err: any) {
      Toaster().fire({
        icon: 'error',
        title: err?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} px={2} py={1}>
      <Grid container spacing={2}>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Agency name'
            id='agency-name'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('agency_name')}
          />
        </Grid>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Company email'
            id='company-email'
            type='email'
            InputLabelProps={{ shrink: true }}
            {...register('agency_email')}
          />
        </Grid>

        {/* Change Credential Email Checkbox */}
        <Grid xs={12} md={6} lg={6} item>
          <Paper
            elevation={0}
            variant='outlined'
            sx={{
              p: 1,
              px: 1.5,
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 1,
              borderColor: isChangeEmail ? 'primary.main' : 'divider',
              bgcolor: isChangeEmail
                ? 'rgba(25, 118, 210, 0.04)'
                : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            <FormControlLabel
              sx={{ width: '100%', m: 0 }}
              control={
                <Checkbox
                  checked={isChangeEmail}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsChangeEmail(checked);
                    if (!checked) {
                      setValue('credential_email', '');
                      clearErrors('credential_email');
                    }
                  }}
                  color='primary'
                  sx={{ p: 0.5, mr: 1 }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    Want to change credential email?
                  </Typography>
                  {(application?.credential_email ||
                    application?.agency_email) && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: 'block',
                        fontSize: '0.725rem',
                        lineHeight: 1,
                      }}
                    >
                      current:{' '}
                      {application?.credential_email ||
                        application?.agency_email}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Paper>
        </Grid>

        {/* Credential Email Input (when checkbox is ticked) */}
        {isChangeEmail && (
          <Grid xs={12} md={6} lg={6} item>
            <TextField
              fullWidth
              required
              label='New Credential Email'
              id='credential-email'
              type='email'
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.credential_email)}
              helperText={
                errors.credential_email?.message
                  ? String(errors.credential_email.message)
                  : ''
              }
              {...register('credential_email', {
                required: isChangeEmail
                  ? 'Credential email is required'
                  : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address',
                },
              })}
            />
          </Grid>
        )}  <Grid xs={12} md={6} lg={6} item>
          <MultipleAndSingleImgValidateWithPreview
            mt={'0'}
            fileLimitSize={5}
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Owner photo ( max size 5mb )'
            fieldName='user_member_representative_photo'
            hideCustomLabel
            mode='single'
            imgSerUrl={
              application?.user_member_representative_photo
                ? [application.user_member_representative_photo]
                : []
            }
          />
        </Grid>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Designation'
            id='representative-designation'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('user_member_representative_designation')}
          />
        </Grid>

        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Company mobile number'
            id='company-mobile'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('phone')}
          />
        </Grid>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Company telephone number'
            id='company-telephone'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('cellphone')}
          />
        </Grid>

        <Grid xs={12} md={6} lg={6} item>
          <TextField
            fullWidth
            label='Company website'
            id='company-website'
            type='url'
            InputLabelProps={{ shrink: true }}
            {...register('member_company_website')}
          />
        </Grid>



        {isAgencyNameChanged && (
          <>
            <Grid xs={12} item>
              <Divider sx={{ my: 1 }}>
                <Typography variant='subtitle2' color='primary' fontWeight={600}>
                  Ownership Status & Documents
                </Typography>
              </Divider>
            </Grid>

            <Grid xs={12} md={6} lg={6} item>
              <FormControl fullWidth>
                <InputLabel id='ownership-status-select-label'>
                  Select Ownership status
                </InputLabel>
                <Select
                  {...register('ownership_status')}
                  labelId='ownership-status-select-label'
                  label='Select Ownership status'
                  value={currentOwnership || ''}
                >
                  <MenuItem value='Proprietorship'>Proprietorship</MenuItem>
                  <MenuItem value='Partnership'>Partnership</MenuItem>
                  <MenuItem value='Incorporated'>Incorporated</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {currentOwnership && (
              <>
                {/* 1, 2, 3 Common documents for all ownership types */}
                <Grid xs={12} md={6} lg={6} item>
                  <MultipleAndSingleImgValidateWithPreview
                    mt={'0'}
                    fileLimitSize={5}
                    register={register}
                    clearErrors={clearErrors}
                    setError={setError}
                    errors={errors}
                    label='Civil aviation english copy ( max size 5mb )'
                    fieldName='civil_aviation_english_copy'
                    hideCustomLabel
                    mode='single'
                    imgSerUrl={
                      application?.civil_aviation_english_copy
                        ? [application.civil_aviation_english_copy]
                        : []
                    }
                  />
                </Grid>

                <Grid xs={12} md={6} lg={6} item>
                  <MultipleAndSingleImgValidateWithPreview
                    mt={'0'}
                    fileLimitSize={5}
                    register={register}
                    clearErrors={clearErrors}
                    setError={setError}
                    errors={errors}
                    label='Trade license ( max size 5mb )'
                    fieldName='trade_license'
                    hideCustomLabel
                    mode='single'
                    imgSerUrl={
                      application?.trade_license
                        ? [application.trade_license]
                        : []
                    }
                  />
                </Grid>

                <Grid xs={12} md={6} lg={6} item>
                  <MultipleAndSingleImgValidateWithPreview
                    mt={'0'}
                    fileLimitSize={5}
                    register={register}
                    clearErrors={clearErrors}
                    setError={setError}
                    errors={errors}
                    label='Forwarding letter ( max size 5mb )'
                    fieldName='forwarding_letter'
                    hideCustomLabel
                    mode='single'
                    imgSerUrl={
                      application?.forwarding_letter
                        ? [application.forwarding_letter]
                        : []
                    }
                  />
                </Grid>

                {/* Specific document for Partnership */}
                {currentOwnership === 'Partnership' && (
                  <Grid xs={12} md={6} lg={6} item>
                    <MultipleAndSingleImgValidateWithPreview
                      mt={'0'}
                      fileLimitSize={5}
                      register={register}
                      clearErrors={clearErrors}
                      setError={setError}
                      errors={errors}
                      label='Partnership deed ( max size 5mb )'
                      fieldName='partnership_deed'
                      hideCustomLabel
                      mode='single'
                      imgSerUrl={
                        application?.partnership_deed
                          ? [application.partnership_deed]
                          : []
                      }
                    />
                  </Grid>
                )}

                {/* Specific documents for Incorporated */}
                {currentOwnership === 'Incorporated' && (
                  <>
                    <Grid xs={12} md={6} lg={6} item>
                      <MultipleAndSingleImgValidateWithPreview
                        mt={'0'}
                        fileLimitSize={5}
                        register={register}
                        clearErrors={clearErrors}
                        setError={setError}
                        errors={errors}
                        label='Incorporation certificate ( max size 5mb )'
                        fieldName='incorporation_certificate'
                        hideCustomLabel
                        mode='single'
                        imgSerUrl={
                          application?.incorporation_certificate
                            ? [application.incorporation_certificate]
                            : []
                        }
                      />
                    </Grid>

                    <Grid xs={12} md={6} lg={6} item>
                      <MultipleAndSingleImgValidateWithPreview
                        mt={'0'}
                        fileLimitSize={5}
                        register={register}
                        clearErrors={clearErrors}
                        setError={setError}
                        errors={errors}
                        label='MoA ( max size 5mb )'
                        fieldName='moa'
                        hideCustomLabel
                        mode='single'
                        imgSerUrl={
                          application?.moa ? [application.moa] : []
                        }
                      />
                    </Grid>

                    <Grid xs={12} md={6} lg={6} item>
                      <MultipleAndSingleImgValidateWithPreview
                        mt={'0'}
                        fileLimitSize={5}
                        register={register}
                        clearErrors={clearErrors}
                        setError={setError}
                        errors={errors}
                        label='Form XII ( max size 5mb )'
                        fieldName='form_xii'
                        hideCustomLabel
                        mode='single'
                        imgSerUrl={
                          application?.form_xii ? [application.form_xii] : []
                        }
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </>
        )}

        <Grid xs={12} item>
          <TextField
            fullWidth
            label='Note'
            id='application-note'
            multiline
            rows={2}
            InputLabelProps={{ shrink: true }}
            {...register('note')}
          />
        </Grid>

        <Grid xs={12} item>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel id='application-status-select-label'>
              Select Status
            </InputLabel>
            <Select
              {...register('status')}
              labelId='application-status-select-label'
              label='Select Status'
              defaultValue={application?.status || 'pending'}
            >
              <MenuItem value='pending'>Pending</MenuItem>
              <MenuItem value='approved'>Approved</MenuItem>
              <MenuItem value='rejected'>Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box mt={4}>
        {!loading ? (
          <Button type='submit' fullWidth variant='contained' disableElevation>
            Submit
          </Button>
        ) : (
          <LoadingButton
            loading
            loadingIndicator='Loading...'
            variant='contained'
            fullWidth
            disabled
          >
            <span>Loading...</span>
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
};

export default UpdateAgencyInfoApplication;

